import { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { Line } from "react-chartjs-2";
import { getClientWeights } from "../../services/TraineeService";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import moment from "moment";
import { colors } from "../ui/colors";
import { weeklyAverageDelta } from "../../utils/weeklyAverage";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const HEB_MONTHS = ["ינו׳", "פבר׳", "מרץ", "אפר׳", "מאי", "יוני", "יולי", "אוג׳", "ספט׳", "אוק׳", "נוב׳", "דצמ׳"];

const GREEN = "#01BBA7";

const METRICS = [
  { value: "weight", label: "משקל", unit: 'ק"ג', color: GREEN, secondaryLabel: "מגמה" },
  { value: "steps", label: "צעדים", unit: "", color: GREEN, secondaryLabel: "יעד" },
  { value: "sleep", label: "שינה", unit: "", color: GREEN, secondaryLabel: "יעד" },
];

// `days` sizes the server fetch (AdminWeightsController: startDate = today - days, clamped to 400).
// `months` sizes the client-side trim, in CALENDAR months, so the window matches the trainee-profile
// dialog's seriesStart = seriesEnd.minus{1|3|6}Months / minusYears(1). Using 31/92/184/366-day
// approximations here instead put the two screens' first bucket up to several days apart, which for
// a weekly-bucketed total can pull in or drop an entire week.
// `days` stays slightly generous so the fetch always covers the trimmed window.
const RANGES = [
  { key: "month", label: "חודש", days: 31, months: 1 },
  { key: "3m", label: "3 חודשים", days: 92, months: 3 },
  { key: "6m", label: "6 חודשים", days: 184, months: 6 },
  { key: "year", label: "שנה", days: 366, months: 12 },
];

const MetricPill = ({ active, label, onClick, accent }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      padding: "6px 14px",
      borderRadius: "20px",
      border: `1px solid ${active ? accent : "rgba(15,20,40,.1)"}`,
      background: active ? accent : "#fff",
      color: active ? "#fff" : colors.textSecondary,
      fontSize: 13,
      fontWeight: active ? 700 : 500,
      cursor: "pointer",
      fontFamily: "inherit",
      transition: "all .14s",
      letterSpacing: 0,
      textTransform: "none",
      "&:hover": active ? {} : { borderColor: colors.textSecondary, color: colors.textPrimary },
    }}
  >
    {label}
  </Box>
);

const RangePill = ({ active, label, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      padding: "3px 9px",
      borderRadius: "20px",
      border: `1px solid ${active ? GREEN : "rgba(15,20,40,.1)"}`,
      background: active ? GREEN : "#fff",
      color: active ? "#fff" : colors.textSecondary,
      fontSize: 10.5,
      fontWeight: active ? 700 : 500,
      cursor: "pointer",
      fontFamily: "inherit",
      transition: "all .13s",
      letterSpacing: 0,
      textTransform: "none",
      "&:hover": active ? {} : { borderColor: colors.textSecondary, color: colors.textPrimary },
    }}
  >
    {label}
  </Box>
);

const StatItem = ({ label, value, unit }) => (
  <Box>
    <Typography sx={{ fontSize: 12, color: colors.textSecondary, fontWeight: 500, mb: 0.5 }}>
      {label}:
    </Typography>
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
      <Typography sx={{ fontSize: 16, color: colors.textPrimary, fontWeight: 700 }}>
        {value == null || value === "" ? "לא זמין" : typeof value === "number" ? value.toLocaleString() : value}
      </Typography>
      {unit && <Typography sx={{ fontSize: 13, color: colors.textSecondary, fontWeight: 500 }}>{unit}</Typography>}
    </Box>
  </Box>
);

const trendLineData = (values) => {
  const n = values.length;
  if (n < 2) return null;
  const xs = values.map((_, i) => i);
  const meanX = xs.reduce((a, b) => a + b, 0) / n;
  const meanY = values.reduce((a, b) => a + b, 0) / n;
  let num = 0;
  let den = 0;
  for (let i = 0; i < n; i++) {
    num += (xs[i] - meanX) * (values[i] - meanY);
    den += (xs[i] - meanX) * (xs[i] - meanX);
  }
  const slope = den === 0 ? 0 : num / den;
  const intercept = meanY - slope * meanX;
  return xs.map((x) => +(intercept + slope * x).toFixed(2));
};

/**
 * The headline "current" value for a metric.
 * - weight: the server's `data.weight` — already the last recorded weight of ANY date
 *   (see AdminWeightsController fallback), so a trainee whose last weigh-in predates the
 *   visible range still shows their real weight instead of "לא זמין".
 * - steps/sleep: the latest in-range record carrying that metric.
 * Returns null when unavailable. `formatSleep` lets tests inject the formatter.
 */
export const currentMetricValue = (data, metric, formatSleep = (m) => m) => {
  const records = data?.records || [];
  if (metric === "weight") return data?.weight ?? null;
  if (records.length === 0) return null;
  if (metric === "steps") return [...records].reverse().find((r) => r.steps != null)?.steps ?? null;
  if (metric === "sleep") {
    const last = [...records].reverse().find((r) => r.sleepMinutes != null);
    return last?.sleepMinutes ? formatSleep(last.sleepMinutes) : null;
  }
  return null;
};

export const extractPoints = (records, metric) => {
  if (!Array.isArray(records)) return [];
  return records
    .filter((r) => {
      if (metric === "weight") return r.data != null;
      if (metric === "steps") return r.steps != null;
      if (metric === "sleep") return r.sleepMinutes != null;
      return false;
    })
    .map((r) => ({
      date: r.date,
      value:
        metric === "weight"
          ? r.data
          : metric === "steps"
            ? r.steps
            : Math.round((r.sleepMinutes / 60) * 10) / 10,
    }));
};

// Trim to the selected range, anchored to the END OF THE CURRENT WEEK (Saturday) — the same anchor
// the trainee-profile dialog uses (CoachDashboardService.computeWeight: seriesEnd = weekEnd, then
// seriesStart = seriesEnd.minusMonths(n)).
//
// Two earlier anchors were wrong, in order:
//  1. The LAST DATA POINT, which silently shortened the window for anyone who hadn't weighed in
//     recently — a trainee whose last weigh-in was 10 days ago got a month that began 41 days back
//     and ended 10 days back.
//  2. `now`. Closer, but still half a week off the profile's anchor. On Wed 29/07 the profile counts
//     back from Sat 01/08 and cuts at 01/07, while `now` cuts at 29/06. Both cutoffs sit inside the
//     SAME Sunday-28/06 bucket, so the charts look aligned — but the bucket's MEMBERSHIP differs
//     (the profile's holds 01/07-04/07; `now`'s also holds 29/06 and 30/06). A partial first bucket
//     averages different days, which is exactly the residual ~0.1 kg gap between the two "סה״כ"
//     badges after the weekly-average fix.
//
// Anchoring both to weekEnd makes the first bucket contain the same days on both screens.
// Everything here works in UTC on purpose. `record.date` is a CALENDAR DAY encoded as UTC midnight
// (AdminWeightsController.toWeightTrackRecord: date.atStartOfDay(ZoneOffset.UTC)), so re-localizing
// it would shift the day: at UTC+3, moment("2026-07-01") is 2026-06-30T21:00Z, which buckets into
// the PREVIOUS week and sits below a same-day cutoff. Comparing UTC-to-UTC keeps calendar days
// whole, and matches toWeeklyAverages, which reads UTC parts for the same reason.
export const startOfCurrentWeekEnd = (nowSecs = Math.floor(Date.now() / 1000)) => {
  const now = moment.utc(nowSecs * 1000);
  // Sunday-anchored week, matching the server (today.minusDays(dayOfWeek % 7), then +6 days).
  // startOf("day") matters too: weigh-ins sit at midnight, so carrying `now`'s time-of-day through
  // would push the boundary hours into the day and drop the weigh-in landing exactly on it.
  return now.clone().startOf("day").subtract(now.day(), "days").add(6, "days");
};

export const filterByRange = (points, rangeKey, nowSecs = Math.floor(Date.now() / 1000)) => {
  const range = RANGES.find((r) => r.key === rangeKey) || RANGES[0];
  if (points.length === 0) return [];
  const cutoff = startOfCurrentWeekEnd(nowSecs).subtract(range.months, "months").unix();
  return points.filter((p) => p.date >= cutoff);
};

const UNIT_LABEL = {
  weight: 'ק"ג',
  steps: "צעדים",
  sleep: "שעות",
};

const formatTooltipValue = (metric, v) => {
  if (v == null) return "לא זמין";
  if (metric === "weight") return `${(+v).toFixed(1)} ק"ג`;
  if (metric === "steps") return `${Math.round(v).toLocaleString()} צעדים`;
  if (metric === "sleep") return `${(+v).toFixed(1)} שעות`;
  return v;
};

const formatSleepDuration = (totalMinutes) => {
  if (totalMinutes == null) return null;
  const h = Math.floor(totalMinutes / 60);
  const m = Math.round(totalMinutes % 60);
  if (h > 0 && m > 0) return `${h} שעה ו-${m} דקות`;
  if (h > 0) return `${h} שעה`;
  return `${m} דקות`;
};

const formatTickValue = (metric, v) => {
  if (v == null) return "";
  if (metric === "weight") return (+v).toFixed(1);
  if (metric === "steps") return Math.round(v).toLocaleString();
  if (metric === "sleep") return (+v).toFixed(1);
  return v;
};

const formatHebDate = (epochSecs) => {
  const d = moment.unix(epochSecs);
  return `${d.date()} ${HEB_MONTHS[d.month()]} ${d.year()}`;
};

const MetricsChart = ({ data, userId, chartHeight = 200 }) => {
  const [metric, setMetric] = useState("weight");
  const [range, setRange] = useState("month");
  const [overrideData, setOverrideData] = useState(null);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (userId == null) {
      setOverrideData(null);
      return;
    }
    // "חודש" fetches like every other range. It used to short-circuit and reuse the `data` prop,
    // which the parent loads WITHOUT a `days` param — and that response covers only the range of
    // getFourRecentWeeks (4 weeks, anchored to weekly-goal rows), not a month. So the month view
    // silently showed a shorter window than the trainer profile's month (09/07 vs 28/6 for the same
    // trainee), which is most of why the two "סה״כ" badges disagreed.
    const days = (RANGES.find((r) => r.key === range) || RANGES[0]).days;
    let cancelled = false;
    setFetching(true);
    getClientWeights(userId, days)
      .then((res) => {
        if (cancelled) return;
        setOverrideData(res?.data || null);
      })
      .catch(() => {
        if (!cancelled) setOverrideData(null);
      })
      .finally(() => {
        if (!cancelled) setFetching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range, userId]);

  const effectiveData = overrideData != null ? overrideData : data;
  const metricDef = METRICS.find((m) => m.value === metric);
  const records = useMemo(() => effectiveData?.records || [], [effectiveData]);

  const allPoints = useMemo(() => extractPoints(records, metric), [records, metric]);
  const points = useMemo(() => filterByRange(allPoints, range), [allPoints, range]);

  const labels = points.map((p) => moment.unix(p.date).format("DD/MM"));
  const values = points.map((p) => p.value);
  const trend = useMemo(() => trendLineData(values), [values]);

  const currentValue = useMemo(
    () => currentMetricValue(effectiveData, metric, formatSleepDuration),
    [metric, effectiveData]
  );

  const secondaryValue = useMemo(() => {
    if (metric === "weight") {
      const progress = effectiveData?.progress;
      if (progress == null) return null;
      return `‎${progress > 0 ? "+" : ""}${progress.toFixed(1)}`;
    }
    if (metric === "steps") return effectiveData?.stepsGoal;
    if (metric === "sleep") {
      const mins = effectiveData?.sleepGoalMinutes;
      if (mins) return formatSleepDuration(mins);
    }
    return null;
  }, [effectiveData, metric]);

  // "סה״כ" compares the FIRST weekly average in the range to the MOST RECENT one — never two raw
  // weigh-ins. Subtracting individual readings let a single noisy morning (water, meal timing) move
  // a monthly headline number, and it disagreed with the trainer profile, which has always used
  // weekly averages: the same trainee showed ↓1.0 here and ↓1.3 there for the same "חודש".
  // toWeeklyAverages mirrors the server's Sunday-anchored bucketing so both screens now agree.
  const delta = useMemo(
    () => (metric === "weight" ? weeklyAverageDelta(points) : null),
    [points, metric],
  );

  const chart = useMemo(() => {
    if (points.length === 0) return null;
    const accent = metricDef.color;
    const datasets = [
      {
        label: metricDef.label,
        data: values,
        borderColor: accent,
        backgroundColor: `${accent}22`,
        borderWidth: 2.5,
        pointRadius: 3.5,
        pointHoverRadius: 5,
        pointBackgroundColor: accent,
        pointBorderColor: "#fff",
        pointBorderWidth: 1.5,
        tension: 0.35,
        fill: true,
      },
    ];
    if (trend) {
      datasets.push({
        label: "מגמה",
        data: trend,
        borderColor: "rgba(100,100,100,0.4)",
        borderDash: [5, 4],
        borderWidth: 1.5,
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0,
        fill: false,
      });
    }
    return {
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 600 },
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "#fff",
            titleColor: colors.textSecondary,
            bodyColor: colors.textPrimary,
            borderColor: "rgba(15,20,40,.1)",
            borderWidth: 1,
            padding: 8,
            cornerRadius: 8,
            displayColors: false,
            titleFont: { size: 10, weight: "600" },
            bodyFont: { size: 11, weight: "700" },
            callbacks: {
              title: (items) => {
                if (!items || items.length === 0) return "";
                const idx = items[0].dataIndex;
                return formatHebDate(points[idx].date);
              },
              label: (ctx) => `${ctx.dataset.label}: ${formatTooltipValue(metric, ctx.parsed.y)}`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: {
              font: { size: 10 },
              color: colors.textSecondary,
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 6,
            },
          },
          y: {
            grid: { color: "rgba(0,0,0,.04)" },
            border: { display: false },
            ticks: {
              font: { size: 10 },
              color: colors.textSecondary,
              padding: 8,
              callback: (v) => formatTickValue(metric, v),
            },
          },
        },
      },
    };
  }, [points, values, trend, labels, metric, metricDef]);

  const deltaColor = delta == null || delta === 0 ? colors.textSecondary : GREEN;

  const sectionHeader = (
    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
      <Box sx={{ display: "flex", gap: 0.75 }}>
        {METRICS.map((m) => (
          <MetricPill
            key={m.value}
            active={metric === m.value}
            label={m.label}
            onClick={() => setMetric(m.value)}
            accent={m.color}
          />
        ))}
      </Box>
      {delta != null && metric === "weight" && (
        <Box
          sx={{
            fontSize: 10.5,
            fontWeight: 800,
            color: deltaColor,
            background: `${deltaColor}15`,
            border: `1px solid ${deltaColor}40`,
            padding: "2px 8px",
            borderRadius: "10px",
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <Box component="span" sx={{ color: colors.textSecondary, fontWeight: 600 }}>סה״כ</Box>
          {delta > 0 ? "↑" : delta < 0 ? "↓" : "•"} {Math.abs(delta).toFixed(1)} ק"ג
        </Box>
      )}
      <Box sx={{ flex: 1 }} />
      <Box sx={{ display: "flex", gap: 0.5 }}>
        {RANGES.map((r) => (
          <RangePill
            key={r.key}
            active={range === r.key}
            label={r.label}
            onClick={() => setRange(r.key)}
          />
        ))}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
      <Box sx={{ display: "flex", gap: { xs: 2, md: 3 }, mb: 1.5 }}>
        <StatItem
          label={metric === "weight" ? "משקל נוכחי" : metric === "steps" ? "צעדים אחרונים" : "שינה אחרונה"}
          value={currentValue}
          unit={metric === "weight" ? 'ק"ג' : ""}
        />
        <StatItem
          label={metric === "weight" ? "מגמה שבועית" : "יעד"}
          value={secondaryValue}
          unit={metric === "weight" ? 'ק"ג' : ""}
        />
      </Box>
      {sectionHeader}
      {chart ? (
        <Box
          sx={{
            background: "#fff",
            border: "1px solid rgba(15,20,40,.08)",
            borderRadius: "10px",
            padding: "14px",
            boxShadow: "0 1px 3px rgba(15,20,40,.04)",
            flex: 1,
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 12,
              fontSize: 10,
              color: colors.textSecondary,
              fontWeight: 600,
              letterSpacing: 0,
            }}
          >
            {UNIT_LABEL[metric]}
          </Box>
          <Box sx={{ height: chartHeight, direction: "ltr", opacity: fetching ? 0.5 : 1, transition: "opacity .15s" }}>
            <Line data={chart.data} options={chart.options} />
          </Box>
          {fetching && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }}
            >
              <CircularProgress size={22} />
            </Box>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            background: "#fff",
            border: "1px dashed rgba(15,20,40,.12)",
            borderRadius: "10px",
            padding: "30px",
            textAlign: "center",
            color: colors.textSecondary,
            fontSize: 13,
          }}
        >
          אין נתוני {metricDef.label} בטווח זה
        </Box>
      )}
    </Box>
  );
};

export default MetricsChart;
